import { MouseEvent, useEffect, useRef, useState } from 'react'

import { useBodyScrollLock } from '@/lib/core/hooks/useBodyScrollLock'
import { usePopover } from '@/lib/core/hooks/usePopover'

import { useTaskProgress } from '../../api'
import { TaskDefinition } from '../../types/task'

export const useTaskStopsPopout = (task: TaskDefinition) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  // Only becomes true once the user opens the popout — keeps the progress endpoint from being
  // fetched for every row in the table on page load.
  const [hasOpened, setHasOpened] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const { positionPopover } = usePopover({
    buttonRef,
    dialogRef,
    defaultPopoverDimensions: { width: 360, height: 320 },
  })

  const {
    data: progress,
    error,
    isLoading,
  } = useTaskProgress(hasOpened ? task.taskDefinition.id : null)

  const showPopover = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setHasOpened(true)
    positionPopover()
    dialogRef.current?.showPopover()
  }

  const closePopover = () => {
    dialogRef.current?.hidePopover()
  }

  // Track open/closed via the dialog's own toggle event rather than only in `showPopover`/
  // `closePopover`, so ESC and outside-click dismissals (which bypass those handlers) still
  // release the scroll lock below.
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleToggle = (event: Event) => {
      setIsOpen((event as ToggleEvent).newState === 'open')
    }

    dialog.addEventListener('toggle', handleToggle)
    return () => dialog.removeEventListener('toggle', handleToggle)
  }, [])

  // `positionPopover` runs once at click-time, while the dialog is still showing its loading
  // state (short/empty). Once the trip details load in, the dialog grows a lot taller, so
  // re-run positioning then — otherwise a popout opened near the bottom of the table renders
  // partly below the viewport.
  useEffect(() => {
    if (!isOpen) return
    positionPopover()
  }, [isOpen, isLoading, error, progress, positionPopover])

  // Lock page scroll while the popout is open — the scrollbar-width compensation matters here in
  // particular, since a viewport shift would move the popout itself off its anchor.
  useBodyScrollLock(isOpen)

  return {
    buttonRef,
    dialogRef,
    progress,
    isLoadingProgress: isLoading,
    hasProgressError: !!error,
    showPopover,
    closePopover,
  }
}
